import { useState, useEffect } from "react";
import { MousePointer, Move, AlertCircle, Info, CheckCircle, Image } from "lucide-react";

import ButtonSelector from "./ButtonSelector";
import ButtonPositionEditor from "./ButtonPositionEditor";
import ImageSelector from "./ImageSelector";
import ImageReplacementEditor from "./ImageReplacementEditor";
import styles from "./styles.module.css";

export default function ElementTestConfig({
    testType,
    formData,
    onUpdate,
    onNext,
    initialConfig = null
}) {
    // Local state for element configuration
    const [elementType, setElementType] = useState('button'); // 'button' or 'image'
    const [selectedButton, setSelectedButton] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [positionConfig, setPositionConfig] = useState(null);
    const [replacementConfig, setReplacementConfig] = useState(null);
    const [activeTab, setActiveTab] = useState('element-type'); // 'element-type', 'button', 'position', 'image', 'replacement'
    const [errors, setErrors] = useState({});

    // Load initial config if provided (when user comes back from next step)
    useEffect(() => {
        if (initialConfig) {
            // Determine element type from config
            if (initialConfig.selectedButton) {
                setElementType('button');
                setSelectedButton(initialConfig.selectedButton);
                setPositionConfig(initialConfig.positionConfig);
                setActiveTab('position');
            } else if (initialConfig.selectedImage) {
                setElementType('image');
                setSelectedImage(initialConfig.selectedImage);
                setReplacementConfig(initialConfig.replacementConfig);
                setActiveTab('replacement');
            }
        }
    }, [initialConfig]);

    // Element type options
    const elementTypes = [
        { id: 'button', label: 'Button Position', icon: MousePointer, description: 'Test moving buttons to different positions' },
        // { id: 'image', label: 'Image Replacement', icon: Image, description: 'Test replacing images with different versions' }
    ];

    // Available tabs based on element type
    const getTabsForElementType = () => {
        const baseTabs = [
            { id: 'element-type', label: 'Element Type', icon: Info }
        ];

        if (elementType === 'button') {
            return [
                ...baseTabs,
                { id: 'button', label: 'Select Button', icon: MousePointer },
                { id: 'position', label: 'Position', icon: Move }
            ];
        } else if (elementType === 'image') {
            return [
                ...baseTabs,
                { id: 'image', label: 'Select Image', icon: Image },
                { id: 'replacement', label: 'Replacement', icon: CheckCircle }
            ];
        }

        return baseTabs;
    };

    const tabs = getTabsForElementType();

    const handleElementTypeChange = (newElementType) => {
        // Clear existing data when switching element types
        setElementType(newElementType);
        setSelectedButton(null);
        setSelectedImage(null);
        setPositionConfig(null);
        setReplacementConfig(null);
        setErrors({});

        // Move to appropriate next tab
        if (newElementType === 'button') {
            setActiveTab('button');
        } else if (newElementType === 'image') {
            setActiveTab('image');
        }
    };

    const handleButtonSelect = (buttonConfig) => {
        setSelectedButton(buttonConfig);
        setActiveTab('position'); // Move to position step
        setErrors({});
    };

    const handleImageSelect = (imageConfig) => {
        setSelectedImage(imageConfig);
        setActiveTab('replacement'); // Move to replacement step
        setErrors({});
    };

    const handlePositionUpdate = (positionData) => {
        setPositionConfig(positionData);

        // Clear position-related errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.position;
            return newErrors;
        });
    };

    const handleReplacementUpdate = (replacementData) => {
        setReplacementConfig(replacementData);

        // Clear replacement-related errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.replacement;
            return newErrors;
        });
    };

    const validateConfiguration = () => {
        const newErrors = {};

        if (elementType === 'button') {
            // Validate button selection
            if (!selectedButton) {
                newErrors.button = 'Please select a button to test';
            }

            // Validate position configuration
            if (!positionConfig || !positionConfig.test?.targetSelector) {
                newErrors.position = 'Please configure where to move the button';
            }
        } else if (elementType === 'image') {
            // Validate image selection
            if (!selectedImage) {
                newErrors.image = 'Please select an image to test';
            }

            // Validate replacement configuration
            if (!replacementConfig || !replacementConfig.test?.imageUrl) {
                newErrors.replacement = 'Please configure the replacement image';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleContinue = () => {
        if (!validateConfiguration()) {
            return;
        }

        // Update parent with element configuration
        const elementConfig = {
            elementType,
            ...(elementType === 'button' && {
                selectedButton,
                modifications: {
                    selector: selectedButton.selector,
                    buttonType: selectedButton.type,
                    buttonName: selectedButton.name,
                    pageUrl: selectedButton.pageUrl,
                    style: {
                        original: {},
                        test: {}
                    },
                    text: {
                        original: '',
                        test: ''
                    },
                    position: positionConfig ? positionConfig : {
                        original: {
                            targetName: '',
                            targetSelector: '',
                            position: 'current'
                        },
                        test: {
                            targetName: positionConfig?.test?.targetName || '',
                            targetSelector: positionConfig?.test?.targetSelector || '',
                            position: positionConfig?.test?.position || 'current'
                        }
                    }
                }
            }),
            ...(elementType === 'image' && {
                selectedImage,
                modifications: {
                    selector: selectedImage.selector,
                    imageName: selectedImage.name,
                    pageUrl: selectedImage.pageUrl,
                    imageReplacement: replacementConfig
                }
            })
        };

        onUpdate({
            elementConfig
        });

        // Move to next step
        onNext();
    };

    const hasValidConfig = () => {
        if (elementType === 'button') {
            return selectedButton &&
                positionConfig &&
                positionConfig.test?.targetSelector &&
                Object.keys(errors).length === 0;
        } else if (elementType === 'image') {
            return selectedImage &&
                replacementConfig &&
                replacementConfig.test?.imageUrl &&
                Object.keys(errors).length === 0;
        }
        return false;
    };

    const getCurrentTabComponent = () => {
        switch (activeTab) {
            case 'element-type':
                return (
                    <div className={styles.elementTypeSelector}>
                        <h4 className={styles.selectorTitle}>Choose Element Test Type</h4>
                        <div className={styles.elementTypeGrid}>
                            {elementTypes.map((type) => (
                                <button
                                    key={type.id}
                                    onClick={() => handleElementTypeChange(type.id)}
                                    className={`${styles.elementTypeCard} ${elementType === type.id ? styles.selected : ''}`}
                                >
                                    <type.icon size={24} className={styles.elementTypeIcon} />
                                    <h5 className={styles.elementTypeLabel}>{type.label}</h5>
                                    <p className={styles.elementTypeDescription}>{type.description}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 'button':
                return (
                    <ButtonSelector
                        selectedButton={selectedButton}
                        onButtonSelect={handleButtonSelect}
                    />
                );
            case 'position':
                return selectedButton ? (
                    <ButtonPositionEditor
                        buttonConfig={selectedButton}
                        positionData={positionConfig}
                        onUpdate={handlePositionUpdate}
                        errors={errors.position}
                    />
                ) : (
                    <div className={styles.emptyState}>
                        <AlertCircle size={48} className={styles.emptyIcon} />
                        <h4>Select a Button First</h4>
                        <p>Please go back to the "Select Button" tab and choose a button to configure its position.</p>
                    </div>
                );
            case 'image':
                return (
                    <ImageSelector
                        selectedImage={selectedImage}
                        onImageSelect={handleImageSelect}
                    />
                );
            case 'replacement':
                return selectedImage ? (
                    <ImageReplacementEditor
                        imageConfig={selectedImage}
                        replacementData={replacementConfig}
                        onUpdate={handleReplacementUpdate}
                        errors={errors.replacement}
                    />
                ) : (
                    <div className={styles.emptyState}>
                        <AlertCircle size={48} className={styles.emptyIcon} />
                        <h4>Select an Image First</h4>
                        <p>Please go back to the "Select Image" tab and choose an image to configure its replacement.</p>
                    </div>
                );
            default:
                return null;
        }
    };

    const getTabTitle = () => {
        if (elementType === 'button') {
            return "Configure Button Position Test";
        } else if (elementType === 'image') {
            return "Configure Image Replacement Test";
        }
        return "Configure Element Test";
    };

    const getTabDescription = () => {
        if (elementType === 'button') {
            return "Test moving buttons to different locations on the page to optimize conversions";
        } else if (elementType === 'image') {
            return "Test replacing images to see which versions perform better";
        }
        return "Choose the type of element test you want to run";
    };

    return (
        <div className={styles.container}>
            <div className={styles.configCard}>
                <div className={styles.configHeader}>
                    <h3 className={styles.configTitle}>{getTabTitle()}</h3>
                    <div className={styles.infoBox}>
                        <Info size={16} />
                        <span>{getTabDescription()}</span>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className={styles.tabNavigation}>
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        const isDisabled =
                            (tab.id === 'position' && !selectedButton) ||
                            (tab.id === 'replacement' && !selectedImage) ||
                            (tab.id === 'button' && elementType !== 'button') ||
                            (tab.id === 'image' && elementType !== 'image');

                        return (
                            <button
                                key={tab.id}
                                onClick={() => !isDisabled && setActiveTab(tab.id)}
                                className={`${styles.tabButton} ${isActive ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}
                                disabled={isDisabled}
                            >
                                <Icon size={16} />
                                <span>{tab.label}</span>
                                {(tab.id === 'button' && selectedButton) ||
                                    (tab.id === 'position' && positionConfig) ||
                                    (tab.id === 'image' && selectedImage) ||
                                    (tab.id === 'replacement' && replacementConfig) ? (
                                    <div className={styles.tabIndicator}>✓</div>
                                ) : null}
                            </button>
                        );
                    })}
                </div>

                {/* Tab Content */}
                <div className={styles.tabContent}>
                    {getCurrentTabComponent()}
                </div>

                {/* Configuration Summary */}
                {hasValidConfig() && (
                    <div className={styles.summarySection}>
                        <h4 className={styles.summaryTitle}>Test Configuration Summary</h4>
                        <div className={styles.summaryContent}>
                            {elementType === 'button' && selectedButton && positionConfig && (
                                <>
                                    <div className={styles.summaryItem}>
                                        <strong>Button:</strong> {selectedButton.name}
                                        <code className={styles.summaryCode}>{selectedButton.selector}</code>
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <strong>Move to:</strong> {positionConfig.test.targetName}
                                        <code className={styles.summaryCode}>{positionConfig.test.targetSelector}</code>
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <strong>Position:</strong> {positionConfig.test.position}
                                    </div>
                                </>
                            )}
                            {elementType === 'image' && selectedImage && replacementConfig && (
                                <>
                                    <div className={styles.summaryItem}>
                                        <strong>Image:</strong> {selectedImage.name}
                                        <code className={styles.summaryCode}>{selectedImage.selector}</code>
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <strong>New Image:</strong> {replacementConfig.test.imageUrl}
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <strong>Upload Method:</strong> {replacementConfig.test.uploadMethod || 'url'}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Summary & Actions */}
                <div className={styles.actionsSection}>
                    <div className={styles.summaryInfo}>
                        {hasValidConfig() && (
                            <div className={styles.successMessage}>
                                <CheckCircle size={16} />
                                <span>Element test configuration is complete. You can proceed to the next step.</span>
                            </div>
                        )}
                        {Object.keys(errors).length > 0 && (
                            <div className={styles.errorSummary}>
                                <AlertCircle size={16} />
                                <span>Please fix the errors above before continuing.</span>
                            </div>
                        )}
                        {!hasValidConfig() && Object.keys(errors).length === 0 && (
                            <div className={styles.warningMessage}>
                                <AlertCircle size={16} />
                                <span>Please complete the element configuration to proceed.</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.actions}>
                        <button
                            onClick={() => {
                                setSelectedButton(null);
                                setSelectedImage(null);
                                setPositionConfig(null);
                                setReplacementConfig(null);
                                setActiveTab('element-type');
                                setErrors({});
                            }}
                            className={styles.secondaryButton}
                            disabled={!selectedButton && !selectedImage}
                        >
                            Reset Configuration
                        </button>
                        <button
                            onClick={handleContinue}
                            className={styles.primaryButton}
                            disabled={!hasValidConfig()}
                        >
                            Continue to Test Settings
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
